import { Box, Button, Card, Flex, Grid, Icon, Text } from '@chakra-ui/react'
import React, { useContext, useMemo, useState, useEffect } from 'react'
import { text } from '../../themes/styles'
import { CircleFlag } from 'react-circle-flags'
import { useTranslation } from 'react-i18next'
import { PlusIcon, Trash04Icon, Users02Icon } from '../../assets/icons'
import { getCountryNameByCode, phoneNumbers } from '../countries/PhoneDial'
import DiscardModal from '../modals/discardModal'
import AddTeamsModal from '../modals/addTeams'
import LoadingTable from './base/LoadingTable'
import MasterTable, { Column, RowData } from './base/MasterTable'
import Pagination from './base/Pagination'
import SearchInput from './base/SearchInput'
import FilterButton from './base/FilterButton'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { deleteTeam, fetchTeamList } from '../../api/workforce/workforce'
import { AuthContext } from '../../hooks/auth/useAuth'
import { useNavigate } from 'react-router-dom'
import AvatarCell from './base/AvatarCell'
import { prefetchTeamRowIntent } from '../../lib/performance/prefetch'

interface TableComponentProps {
  setData: React.Dispatch<React.SetStateAction<RowData[]>>
  isUserMode?: boolean
}

const TeamTable: React.FC<TableComponentProps> = ({ setData, isUserMode = false }) => {
  const { t } = useTranslation()
  const auth = useContext(AuthContext)
  const currentUser = auth?.user

  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState<{ [key: string]: string[] }>({})
  const COUNTRY_OPTIONS = useMemo(() => phoneNumbers.map(c => ({ id: c.code, name: c.name })), [])
  const [showDiscardModal, setShowDiscardModal] = useState(false)
  const [selectedRows, setSelectedRows] = useState<string[]>([])
  const [isAddTeamsOpen, setIsAddTeamsOpen] = useState(false)

  const [page, setPage] = useState(1)
  const itemsPerPage = 15
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Build filters - add members filter in user mode (user from auth context)
  const teamFilters = useMemo(() => {
    if (isUserMode && currentUser?.id) {
      return { ...filters, members: [currentUser.id.toString()] }
    }
    return filters
  }, [filters, isUserMode, currentUser?.id])
  const teamFiltersKey = useMemo(() => JSON.stringify(teamFilters), [teamFilters])

  const teams = useQuery({
    queryKey: ['teams', page, searchTerm, teamFiltersKey, isUserMode],
    // keep prefetched pages fresh briefly to avoid immediate refetch on navigation
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
    queryFn: () =>
      fetchTeamList({
        search: searchTerm,
        page,
        pageSize: itemsPerPage,
        filters: teamFilters,
        // only fetch name, country, members (ids) and expanded team_manager details
        expand: 'team_manager',
        fields:
          'id,name,country,members,team_manager.id,team_manager.first_name,team_manager.last_name,team_manager.profile_picture,team_manager.work_email',
      }),
  })

  // Prefetch next teams page when current page looks full
  useEffect(() => {
    const currentCount = teams.data?.data?.length ?? 0
    if (currentCount < itemsPerPage) return

    const nextPage = page + 1

    queryClient.prefetchQuery({
      queryKey: ['teams', nextPage, searchTerm, teamFiltersKey, isUserMode],
      staleTime: 60 * 1000,
      queryFn: () =>
        fetchTeamList({
          search: searchTerm,
          page: nextPage,
          pageSize: itemsPerPage,
          filters: teamFilters,
          expand: 'team_manager',
          fields:
            'id,name,country,members,team_manager.id,team_manager.first_name,team_manager.last_name,team_manager.profile_picture,team_manager.work_email',
        }),
    })
  }, [teams.data, page, searchTerm, teamFilters, teamFiltersKey, queryClient, itemsPerPage, isUserMode])

  const deleteTeamMutation = useMutation({
    mutationFn: deleteTeam,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] })
    },
  })

  const handleDeleteTeams = () => {
    setShowDiscardModal(true)
  }

  const handleConfirmDelete = () => {
    selectedRows.forEach((teamId) => {
      deleteTeamMutation.mutate(teamId, {
        onSuccess: () => {
          setData((prevData) => prevData.filter((row) => String(row.id) !== teamId))
          queryClient.invalidateQueries({ queryKey: ['teams'] })
        },
      })
    })
    setSelectedRows([])
    setShowDiscardModal(false)
  }

  const columns: Column[] = [
    ...(!isUserMode ? [{ label: '', accessor: 'select', type: 'select' as const, width: '5%' }] : []),
    {
      label: t('workforce.teams.teamName'),
      accessor: 'name',
      type: 'string',
      width: '25%',
      Cell: ({ row }) => (
        <Box>
          <Text {...text.heading} mb="4px" isTruncated>
            {row.name}
          </Text>
          <Text {...text.small} mt="0px">
            <Icon width="16px" height="16px" fill="solid.500" color="solid.500" mr="8px">
              <Users02Icon />
            </Icon>
            {t('workforce.teams.peopleCount', { count: row.members.length })}{' '}
          </Text>
        </Box>
      ),
    },
    {
      label: t('workforce.teams.teamManager'),
      accessor: 'team_manager',
      type: 'string',
      Cell: ({ row }) => (
        <AvatarCell image={row.team_manager?.profile_picture} name={`${row.team_manager?.first_name} ${row.team_manager?.last_name}`} subtitle={row.team_manager?.work_email} />
      ),
    },
    {
      label: t('common.fields.locations'),
      accessor: 'country',
      type: 'custom',
      width: '33%',
      Cell: ({ row }: { row: RowData }) => (
        <Flex alignItems="center">
          {row.country && (
            <CircleFlag
              countryCode={row.country.toLowerCase()}
              style={{ width: '24px', height: '24px', marginRight: '8px' }}
            />
          )}
          <Text ml={2} textStyle="b-xs">
            {getCountryNameByCode(row.country)}
          </Text>
        </Flex>
      ),
    },
    { label: '', accessor: 'actions', type: 'actions' },
  ]

  const teamData = teams?.data?.data || []

  const allSelected = teamData.length > 0 && teamData.every((row: RowData) => selectedRows.includes(String(row.id)))
  const isIndeterminate =
    teamData.length > 0 && teamData.some((row: RowData) => selectedRows.includes(String(row.id))) && !allSelected

  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm)
    setPage(1)
  }

  const updateFilter = (key: string, values: string[]) => {
    setFilters(prev => {
      const next = { ...prev }
      if (values.length === 0) delete next[key]
      else next[key] = values
      return next
    })
    setPage(1)
  }

  const handleSelectAll = (isChecked: boolean) => {
    if (isChecked) {
      setSelectedRows(teamData.map((row: RowData) => String(row.id)))
    } else {
      setSelectedRows([])
    }
  }
  const handleOpenNewTab = (row: RowData) => {
    const path = isUserMode ? `/user/organization/${row.id}` : `/user/workforce/teams/${row.id}`
    window.open(path, '_blank')
  }
  const handleRowClick = (row: RowData) => {
    const path = isUserMode ? `/user/organization/${row.id}` : `/user/workforce/teams/${row.id}`
    navigate(path)
  }
  const handleSelectRow = (id: string | number, isChecked: boolean) => {
    const teamId = String(id)
    if (isChecked) {
      setSelectedRows((prev) => (prev.includes(teamId) ? prev : [...prev, teamId]))
    } else {
      setSelectedRows((prev) => prev.filter((rowId) => rowId !== teamId))
    }
  }

  return (
    <Card width="100%" p="0">
      <Grid>
        <Box>
          <Flex justifyContent="space-between" alignItems="center" height="64px" flexWrap="wrap" gap={4} px="20px">
            <SearchInput onSearch={handleSearch} />
            <Flex gap="8px">
              {!isUserMode && selectedRows.length > 0 ? (
                <Button 
                  onClick={handleDeleteTeams} 
                  variant="error"
                  size="sm"
                  h="32px"
                  px="14px"
                  fontSize="13px"
                  fontWeight="600"
                  borderRadius="8px"
                >
                  <Icon fill="none" color="error.100" mr={2}>
                    <Trash04Icon />
                  </Icon>
                  {t('workforce.people.deleteCount', { count: selectedRows.length })}
                </Button>
              ) : (
                <>
                  <FilterButton
                    label={t('common.fields.country')}
                    options={COUNTRY_OPTIONS}
                    onApply={(v) => updateFilter('country', v)}
                  />
                  {!isUserMode && (
                    <Button 
                      bg="black" 
                      color="white" 
                      onClick={() => setIsAddTeamsOpen(true)} 
                      gap="8px"
                      variant="mono" 
                      size="sm"
                      h="32px"
                      px="14px"
                      fontSize="13px"
                      fontWeight="600"
                      borderRadius="8px"
                    >
                      <Icon w="12px" h="12px" viewBox="0 0 24 24" fill="none" color="white">
                        <PlusIcon />
                      </Icon>
                      {t('workforce.teams.addNewTeam')}
                    </Button>
                  )}
                </>
              )}
            </Flex>
          </Flex>
          {teams.isLoading ? (
            <LoadingTable columns={columns} />
          ) : (
            <Box minHeight="58vh">
              <MasterTable
                columns={columns}
                data={teamData}
                onRowClick={handleRowClick}
                onRowIntent={(row) => prefetchTeamRowIntent(queryClient, row.id)}
                onOpenNewTab={handleOpenNewTab}
                selectedRows={selectedRows}
                onSelectAll={handleSelectAll}
                onSelectRow={handleSelectRow}
                allSelected={allSelected}
                isIndeterminate={isIndeterminate}
                tableType="teams"
              />
            </Box>
          )}
          <Pagination
            currentPage={page}
            itemsPerPage={itemsPerPage}
            maxPage={teams?.data?.max_page || 0}
            totalItems={teams?.data?.count || 0}
            handlePreviousPage={() => setPage(page - 1)}
            handleNextPage={() => setPage(page + 1)}
            handlePageClick={(page: number) => setPage(page)}
          />
        </Box>
      </Grid>
      {!isUserMode && (
        <>
          <DiscardModal
            show={showDiscardModal}
            handleClose={() => setShowDiscardModal(false)}
            handleDelete={handleConfirmDelete}
            action="delete"
            selectedRowsCount={selectedRows.length}
          />
          <AddTeamsModal isOpen={isAddTeamsOpen} onClose={() => setIsAddTeamsOpen(false)} />
        </>
      )}
    </Card>
  )
}

export default TeamTable
